import { FC } from 'react';
import {
  FormControlLabel,
  Radio,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  ListItemText,
} from '@mui/material';

type FieldType = 'text' | 'radio' | 'select' | 'multi-select';

export interface FormFieldI {
  type: FieldType;
  name: string;
  label: string;
  required: boolean;
  options?: { label: string; value: string }[];
}

interface FormFieldComponentProps {
  field: FormFieldI;
  formValues: Record<string, string | string[]>;
  handleInputChange: (name: string, value: string | string[]) => void;
  loading: boolean;
  disabled?: boolean;
}

const FormField: FC<FormFieldComponentProps> = ({
  field,
  formValues,
  handleInputChange,
  loading,
  disabled = false,
}) => {
  switch (field.type) {
    case 'radio':
      return (
        <>
          {field.options?.map((option) => (
            <div key={option.value} className="radio-option">
              <FormControlLabel
                control={
                  <Radio
                    disableFocusRipple
                    disableRipple
                    disableTouchRipple
                    checked={formValues[field.name] === option.value}
                    value={option.value}
                    id={`${field.name}-${option.value}`}
                    name={field.name}
                    onChange={() => handleInputChange(field.name, option.value)}
                  />
                }
                label={
                  <Typography
                    fontSize={14}
                    lineHeight={'1.25rem'}
                    fontWeight={700}
                    color={'#162936'}
                  >
                    {option.label}
                  </Typography>
                }
                htmlFor={`${field.name}-${option.value}`}
              />
            </div>
          ))}
        </>
      );
    case 'select':
      return (
        <FormControl fullWidth disabled={loading || disabled}>
          <InputLabel id={`${field.name}-label`}>{field.label}</InputLabel>
          <Select
            labelId={`${field.name}-label`}
            id={field.name}
            value={formValues[field.name] || ''}
            label={field.label}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            sx={{
              width: '100%',
            }}
            MenuProps={{
              sx: {
                zIndex: 10000,
              },
              PaperProps: {
                sx: {
                  maxHeight: '20rem',
                },
              },
            }}
            disabled={disabled}
          >
            {field.options?.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    case 'multi-select':
      return (
        <FormControl fullWidth disabled={loading}>
          <InputLabel id={`${field.name}-label`}>{field.label}</InputLabel>
          <Select
            labelId={`${field.name}-label`}
            id={field.name}
            multiple
            value={formValues[field.name] || []}
            label={field.label}
            MenuProps={{
              sx: {
                zIndex: 10000,
              },
              PaperProps: {
                sx: {
                  maxHeight: '20rem',
                },
              },
            }}
            onChange={(e) => {
              const newValue = [...e.target.value];
              const isNone = newValue.includes('None');

              if (isNone) {
                const allValuesExceptNone =
                  field.options
                    ?.filter((option) => option.value !== 'None')
                    .map((option) => option.value) || [];

                if (allValuesExceptNone?.length === formValues[field.name]?.length) {
                  // Deselect None
                  handleInputChange(field.name, []);
                } else {
                  // Select None
                  handleInputChange(field.name, allValuesExceptNone);
                }
              } else {
                handleInputChange(field.name, newValue);
              }
            }}
            renderValue={(selected) => {
              const allValuesExceptNone = field.options?.filter(
                (option) => option.value !== 'None',
              );
              if (selected?.length === allValuesExceptNone?.length) {
                return 'None';
              }

              const selectedLabels = field.options
                ?.filter((option) => (selected as string[]).includes(option.value))
                .map((option) => option.label);
              return selectedLabels?.join(', ');
            }}
            sx={{
              width: '100%',
              opacity: disabled ? 0.5 : 1,
            }}
          >
            {field.options?.map((option) => {
              const allValuesExceptNone = field.options?.filter(
                (option) => option.value !== 'None',
              );
              const isNoneSelected = allValuesExceptNone?.length === formValues[field.name]?.length;
              const isNoneOption = option.value === 'None';

              return (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  disabled={(!isNoneOption && isNoneSelected) || disabled}
                >
                  <Checkbox
                    checked={
                      ((formValues[field.name] || []).includes(option.value) &&
                        !isNoneSelected &&
                        !isNoneOption) ||
                      (isNoneOption && isNoneSelected)
                    }
                  />
                  <ListItemText primary={option.label} />
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      );
    case 'text':
    default:
      return (
        <TextField
          id={field.name}
          label={field.label}
          placeholder={field.label}
          variant="outlined"
          onChange={(e) => handleInputChange(field.name, e.target.value)}
          disabled={loading || disabled}
          sx={{
            width: '100%',
          }}
        />
      );
  }
};

export default FormField;
